import { useState } from "react";
import {
  CheckCircle2, CalendarDays, Wallet, Video, Clock, ArrowRight, HeartHandshake, BookOpenCheck,
} from "lucide-react";
import { COURSE_LIST } from "@/data/courses";
import BookingDialog from "@/components/site/BookingDialog";
import LpShell, { useLpMeta, GoogleStarsBadge } from "@/pages/lp/LpShell";
import LpLeadForm from "@/pages/lp/LpLeadForm";
import TrackedPhoneLink from "@/components/site/TrackedPhoneLink";

/**
 * Google-Ads-LP für die Palliativ-Kampagne.
 * Hintergrund: Die alte Anzeige (Palliativbegleiter mit Kim Meiritz) hatte viele
 * Impressionen (281), aber nur 0,71 % CTR – diese LP holt das Potenzial ab.
 * Final-URL: /palliativ
 */
const USPS = [
  { icon: CalendarDays, text: "Start jeden Montag – keine Wartezeit" },
  { icon: Video, text: "100 % online live – E-Learning + Live-Termine" },
  { icon: Wallet, text: "Flexibel zahlen – auch Ratenkauf via Klarna" },
  { icon: BookOpenCheck, text: "Zertifikat Palliative Care (160 Std.)" },
];

const WIRKUNG = [
  ["Fachlich sicher", "Schmerz- & Symptomkontrolle, Pflege Sterbender, rechtliche Grundlagen wie Patientenverfügung – du weißt in jeder Situation, was zu tun ist."],
  ["Menschlich stark", "Kommunikation mit Sterbenden und Angehörigen, Ethik, Spiritualität und kulturelle Aspekte – die Gespräche, vor denen viele Angst haben, werden deine Stärke."],
  ["Gut für dich selbst", "Trauerbegleitung & Selbstfürsorge sind feste Bestandteile – damit du diese wertvolle Arbeit lange und gesund machen kannst."],
];

export default function LpPalliativ() {
  useLpMeta(
    "Palliativ-Care Weiterbildung · 100 % online · Start jeden Montag | Dickies-Campus",
    "Weiterbildung Palliative Care mit erfahrener Palliativspezialistin: 120 UE, online live, Zertifikat. Sofort buchbar – Start jeden Montag."
  );
  const [bookingCourse, setBookingCourse] = useState(null);
  const course = COURSE_LIST.find((c) => c.id === "palliativ-care");

  return (
    <LpShell>
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-12 lg:pt-20">
        <div className="max-w-3xl">
          <GoogleStarsBadge />
          <h1 data-testid="lp-palliativ-headline" className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-sand-800 leading-[1.05]">
            Palliativ-Care Weiterbildung.<br />
            <span className="text-terra">Die wertvollste Aufgabe der Pflege.</span>
          </h1>
          <p className="mt-5 text-sand-600 text-base md:text-lg leading-relaxed max-w-xl">
            Begleite Menschen in ihrer letzten Lebensphase – mit fachlicher und menschlicher
            Kompetenz. <strong className="text-sand-800">120 UE, online live, mit Zertifikat</strong> und
            erfahrener Palliativspezialistin als Dozentin.
          </p>
          <ul className="mt-7 grid sm:grid-cols-2 gap-3 max-w-xl">
            {USPS.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-2.5 text-[14px] text-sand-700">
                <Icon className="h-5 w-5 text-terra shrink-0" strokeWidth={1.7} />
                {text}
              </li>
            ))}
          </ul>
          <div className="mt-9 flex flex-wrap gap-4">
            <a href="#lp-kurs" data-testid="lp-palliativ-cta-course"
              className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark text-white px-7 py-3.5 text-base font-semibold transition-colors group">
              Kurs & Preis ansehen
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.8} />
            </a>
            <a href="#lp-kontakt" data-testid="lp-palliativ-cta-beratung"
              className="inline-flex items-center rounded-full border border-sand-400 hover:border-sand-800 text-sand-800 px-7 py-3.5 text-base font-medium transition-colors">
              Kostenlose Beratung
            </a>
          </div>
        </div>
      </section>

      {/* Was die Weiterbildung mit dir macht */}
      <section className="bg-cream-100 border-y border-cream-300 py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-3xl text-sand-800 mb-8">Was diese Weiterbildung verändert</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {WIRKUNG.map(([t, d]) => (
              <div key={t} className="bg-white rounded-3xl border border-cream-300 p-7">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-terra/10 text-terra">
                  <HeartHandshake className="h-5 w-5" strokeWidth={1.7} />
                </span>
                <h3 className="font-display text-xl text-sand-800 mt-4">{t}</h3>
                <p className="text-[13.5px] text-sand-600 mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kurs-Karte */}
      <section id="lp-kurs" className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        {course && (
          <div className="grid lg:grid-cols-[1fr_0.9fr] gap-10 items-start">
            <div>
              <h2 className="font-display text-3xl text-sand-800 mb-6">Die Inhalte im Überblick</h2>
              <ul className="space-y-3 text-[14.5px] text-sand-700">
                {course.curriculum.map((t) => (
                  <li key={t} className="flex items-start gap-2.5">
                    <CheckCircle2 className="h-[18px] w-[18px] text-terra shrink-0 mt-0.5" strokeWidth={1.8} />{t}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[13.5px] text-sand-600 max-w-xl">
                Nach Abschluss bist du qualifiziert für <strong className="text-sand-800">Hospiz- &
                Palliativdienste, SAPV-Teams und Palliativstationen</strong> – Bereiche mit großem
                Bedarf und besonderer Wertschätzung.
              </p>
            </div>
            <div data-testid="lp-palliativ-course-card"
              className="bg-white border border-cream-300 rounded-3xl p-8 shadow-hover sticky top-24">
              <h3 className="font-display text-[1.4rem] text-sand-800 leading-snug">{course.title}</h3>
              <p className="text-[13px] text-sand-600 mt-1.5">{course.subtitle}</p>
              <div className="mt-5 space-y-2 text-[13.5px] text-sand-700">
                <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-terra" strokeWidth={1.7} /> {course.weeks} · {course.duration}</div>
                <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-terra" strokeWidth={1.7} /> Start: {course.nextStart}</div>
                <div className="flex items-center gap-2"><Video className="h-4 w-4 text-terra" strokeWidth={1.7} /> {course.format}</div>
                <div className="flex items-center gap-2"><Wallet className="h-4 w-4 text-terra" strokeWidth={1.7} /> {course.funding}</div>
              </div>
              <div className="mt-6 pt-5 border-t border-cream-200">
                <div className="flex items-end justify-between mb-5">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-sand-500 font-semibold">Selbstzahler</div>
                    <div className="font-display text-3xl font-semibold text-sand-800">{course.price}</div>
                  </div>
                  <div className="text-right text-[12px] text-sand-500">
                    Ratenkauf möglich<br /><span className="text-terra font-bold text-[14px]">via Klarna</span>
                  </div>
                </div>
                <button type="button" data-testid="lp-palliativ-book" onClick={() => setBookingCourse(course)}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-terra hover:bg-terra-dark text-white h-12 text-[15px] font-semibold transition-colors">
                  Jetzt Platz sichern <ArrowRight className="h-4 w-4" strokeWidth={2} />
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Kontakt */}
      <section id="lp-kontakt" className="bg-brand-rose border-t border-brand-rose py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl text-brand-darkred leading-tight">
              Fragen zur Palliativ-Weiterbildung?<br /><span className="text-white">Wir beraten dich kostenlos.</span>
            </h2>
            <p className="mt-4 text-brand-darkred/80 text-[15px] leading-relaxed max-w-md">
              Wir klären gemeinsam, ob die Weiterbildung zu dir passt und finden die
              beste Finanzierung für dich – ehrlich und ohne Verkaufsdruck.
            </p>
            <TrackedPhoneLink source="lp-palliativ" className="mt-6 inline-flex items-center gap-2 text-lg font-semibold text-brand-darkred hover:text-white transition-colors">
              📞 0211 29273330
            </TrackedPhoneLink>
          </div>
          <LpLeadForm source="ads-lp-palliativ" audience="pflegekraft"
            sub="Sag uns kurz, wo du gerade stehst – wir melden uns mit einer ehrlichen Empfehlung." />
        </div>
      </section>

      <BookingDialog course={bookingCourse} open={!!bookingCourse} onOpenChange={(v) => !v && setBookingCourse(null)}
        initialBuyerType="selbstzahler" />
    </LpShell>
  );
}
